/** @format */

import WidgetFormattedNumber from 'components/PropertyWidget/components/WidgetFormattedNumber';

import {Chart} from 'components/charts/multiline-chart/model/charts';
import {diff as calcDiff} from 'features/mediaplan-progress/helpers/diff';
import {ratioPercent} from 'features/mediaplan-progress/helpers/ratioPercent';
import {sumSeries} from 'features/mediaplan-progress/helpers/sumSeries';
import {WIDGETS_LIST} from 'pages/Home/DigitalCast/constants/widgets';
import getTokenWithSpace from 'pages/Home/DigitalCast/helpers/getTokenWithSpace';
import {ratioFactFromChartData} from 'pages/Home/DigitalCast/mapping/progress-adapter/ratioFactFromChartData';
import {
  factSeriesFromChartData,
  planSeriesFromChartData,
} from 'pages/Home/DigitalCast/mapping/progress-adapter/seriesFromChartData';
import {ChartIds} from 'pages/Home/DigitalCast/models/chartIds';
import {GraphComplexValuesList} from 'pages/Home/DigitalCast/models/common';

import {Column, HeadingText, PlanUnavailableText, Root, ValueBox} from './styles';

interface Props {
  chart: Chart<ChartIds>;
  todayPointName: string;
  graphComplexValuesList: GraphComplexValuesList;
  isPlanUnreachable: boolean;
}

const PlanFact = ({chart, todayPointName, graphComplexValuesList, isPlanUnreachable}: Props) => {
  const chartId = chart.chartId;
  const widget = WIDGETS_LIST[chartId];
  const {
    type,
    complexRatioPredicate,
    token: tokenRaw,
    needSpaceBeforeToken,
    isPositiveRed,
    roundNumberBy,
  } = widget;
  const data = chart.chartsData;

  let fact;
  let plan;
  let diff;
  let diffPercent;

  switch (type) {
    case 'TypeB': {
      if (!complexRatioPredicate) {
        throw new Error('Метод обработки обязателен для виджета этого типа');
      }
      plan = planSeriesFromChartData(data)[todayPointName] ?? 0;
      fact = ratioFactFromChartData({data, graphComplexValuesList, complexRatioPredicate});
      diff = calcDiff({plan, fact});
      diffPercent = ratioPercent(diff, plan);
      break;
    }
    case 'TypeC': {
      if (!complexRatioPredicate) {
        throw new Error('Метод обработки обязателен для виджета этого типа');
      }
      plan = planSeriesFromChartData(data)[todayPointName] ?? 0;
      fact = ratioFactFromChartData({data, graphComplexValuesList, complexRatioPredicate});
      diff = calcDiff({plan, fact});
      break;
    }
    case 'TypeD': {
      fact = sumSeries(factSeriesFromChartData(data));
      plan = sumSeries(planSeriesFromChartData(data));
      diff = calcDiff({plan, fact});
      diffPercent = ratioPercent(diff, plan);
      break;
    }
    default: {
      throw new Error(`Неверный тип виджета: ${type}`);
    }
  }

  if (isPlanUnreachable) {
    plan = 0;
    diff = 0;
    diffPercent = 0;
  }

  const token = getTokenWithSpace({token: tokenRaw, needSpaceBeforeToken});

  const hasPlan = data.some(item => item.line);

  return (
    <Root>
      {hasPlan ? (
        <>
          <Column>
            <HeadingText>План</HeadingText>
            {isPlanUnreachable ? (
              <PlanUnavailableText>Недоступен</PlanUnavailableText>
            ) : (
              <WidgetFormattedNumber number={plan} postfix={token} roundNumberBy={roundNumberBy} />
            )}
          </Column>
          <Column>
            <HeadingText>Факт</HeadingText>
            <WidgetFormattedNumber number={fact} postfix={token} roundNumberBy={roundNumberBy} />
            <WidgetFormattedNumber
              number={diff}
              colorize
              showPlusMinus
              postfix={token}
              isPositiveRed={isPositiveRed}
              roundNumberBy={roundNumberBy}
            />
            {diffPercent !== undefined && (
              <WidgetFormattedNumber
                number={diffPercent}
                colorize
                showPlusMinus
                postfix='%'
                isPositiveRed={isPositiveRed}
                roundNumberBy={1}
              />
            )}
          </Column>
        </>
      ) : (
        <ValueBox>
          <WidgetFormattedNumber number={fact} postfix={token} roundNumberBy={roundNumberBy} />
        </ValueBox>
      )}
    </Root>
  );
};

export {PlanFact};
